import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { BarChart3, Loader2, RefreshCw, ClipboardCheck, Save, MessagesSquare, Activity, Clock, CheckCircle2, ClipboardX, RotateCcw, History, ChevronDown, ChevronRight, Download, ArrowUp, ArrowDown, X } from "lucide-react";

const METRIC_LABELS = {
  asignadas: "Conversaciones asignadas",
  total_atendidas: "Total asignaciones (histórico)",
  activas: "Activas",
  sin_actividad: "Sin actividad",
  sin_encuesta: "Sin encuesta",
  reabiertas: "Reabiertas",
  encuestas_enviadas: "Encuestas enviadas",
  solucionadas: "Solucionadas"
};

const ENCUESTA_DEFAULT = {
  is_active: true,
  pregunta: "Para servirle mejor, ¿nos ayuda con una breve encuesta?\n¿Su solicitud fue resuelta?",
  opcion_solucionada: "Sí, resuelto",
  opcion_inconclusa: "Parcialmente",
  opcion_no_solucionada: "No resuelto",
  mensaje_gracias: "¡Gracias por su respuesta! 🙏 Su opinión nos ayuda a mejorar.",
  inactividad_horas: 24,
  reapertura_alerta_minutos: 5
};

const fmtTiempo = (min) => {
  if (!min) return "—";
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${Math.round(min % 60)}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const Delta = ({ actual, anterior, malo = false }) => {
  if (anterior === undefined || anterior === null) return null;
  const diff = (actual || 0) - anterior;
  if (diff === 0) {
    return <span className="text-[10px] text-slate-400 ml-1" data-testid="stats-delta" title={`Igual que el período anterior (${anterior})`}>=</span>;
  }
  const subio = diff > 0;
  const positivo = malo ? !subio : subio;
  const Icon = subio ? ArrowUp : ArrowDown;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold ml-1 ${positivo ? "text-emerald-600" : "text-red-500"}`}
      data-testid="stats-delta"
      title={`Período anterior: ${anterior} (${subio ? "+" : ""}${diff})`}
    >
      <Icon className="w-3 h-3" />{Math.abs(diff)}
    </span>
  );
};

export default function WhatsAppEstadisticasPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [encuesta, setEncuesta] = useState(ENCUESTA_DEFAULT);
  const [savingEncuesta, setSavingEncuesta] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [detalleMetric, setDetalleMetric] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const fetchDetalle = useCallback(async (metric, d) => {
    try {
      setLoadingDetalle(true);
      const res = await axios.get(`${API}/whatsapp/estadisticas/detalle?metric=${metric}&days=${d}`, getAuthHeaders());
      setDetalleData(res.data);
    } catch {
      toast.error("Error al cargar el detalle");
      setDetalleMetric(null);
      setDetalleData(null);
    } finally { setLoadingDetalle(false); }
  }, [getAuthHeaders]);

  const toggleDetalle = (metric) => {
    if (detalleMetric === metric) {
      setDetalleMetric(null);
      setDetalleData(null);
      return;
    }
    setDetalleMetric(metric);
    setDetalleData(null);
    fetchDetalle(metric, days);
  };

  useEffect(() => {
    if (detalleMetric) fetchDetalle(detalleMetric, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchStats = useCallback(async (d) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/whatsapp/estadisticas/ejecutivos?days=${d}`, getAuthHeaders());
      setData(res.data);
    } catch (err) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchStats(days); }, [fetchStats, days]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/whatsapp/encuesta-satisfaccion/config`, getAuthHeaders())
      .then((res) => setEncuesta({ ...ENCUESTA_DEFAULT, ...res.data }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardarEncuesta = async () => {
    setSavingEncuesta(true);
    try {
      await axios.post(`${API}/whatsapp/encuesta-satisfaccion/config`, {
        is_active: !!encuesta.is_active,
        pregunta: encuesta.pregunta,
        opcion_solucionada: encuesta.opcion_solucionada,
        opcion_inconclusa: encuesta.opcion_inconclusa,
        opcion_no_solucionada: encuesta.opcion_no_solucionada,
        mensaje_gracias: encuesta.mensaje_gracias,
        inactividad_horas: parseInt(encuesta.inactividad_horas, 10) || 24,
        reapertura_alerta_minutos: parseInt(encuesta.reapertura_alerta_minutos, 10) >= 0 ? parseInt(encuesta.reapertura_alerta_minutos, 10) : 5
      }, getAuthHeaders());
      toast.success("Configuración de la encuesta guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingEncuesta(false);
    }
  };

  const ejecutivos = data?.ejecutivos || [];
  const totales = ejecutivos.reduce((a, e) => ({
    asignadas: a.asignadas + e.asignadas, activas: a.activas + e.activas,
    sin_actividad: a.sin_actividad + e.sin_actividad, sin_encuesta: a.sin_encuesta + (e.sin_encuesta || 0),
    reabiertas: a.reabiertas + (e.reabiertas || 0),
    total_atendidas: a.total_atendidas + (e.total_atendidas || 0), concluidas: a.concluidas + (e.concluidas || 0),
    enviadas: a.enviadas + e.encuestas_enviadas, solucionadas: a.solucionadas + e.solucionadas
  }), { asignadas: 0, activas: 0, sin_actividad: 0, sin_encuesta: 0, reabiertas: 0, total_atendidas: 0, concluidas: 0, enviadas: 0, solucionadas: 0 });

  const thCls = "sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 border-b border-r border-slate-200 dark:border-slate-700 text-center whitespace-nowrap";

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-estadisticas-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-green-600" />
            Estadísticas WhatsApp
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Resultados de los ejecutivos y encuesta de satisfacción
          </p>
        </div>

        <Tabs defaultValue="ejecutivos" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="ejecutivos" data-testid="tab-stats-ejecutivos">Ejecutivos</TabsTrigger>
            {isAdmin && <TabsTrigger value="encuesta" data-testid="tab-stats-encuesta">Encuesta de Satisfacción</TabsTrigger>}
          </TabsList>

          <TabsContent value="ejecutivos">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Label className="text-sm">Período:</Label>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger className="w-40 h-9" data-testid="stats-dias-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Últimas 24 horas</SelectItem>
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => fetchStats(days)} data-testid="stats-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" />Actualizar
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/whatsapp/estadisticas/ejecutivos/export?days=${days}`, {
                      ...getAuthHeaders(), responseType: "blob"
                    });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `estadisticas_whatsapp_ejecutivos_${days}dias.xlsx`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { toast.error("Error al exportar Excel"); }
                }}
                className="text-green-700 border-green-300 hover:bg-green-50"
                data-testid="stats-export-excel-btn"
              >
                <Download className="w-4 h-4 mr-1" />Exportar Excel
              </Button>
              {data && (
                <span className="text-xs text-slate-500">
                  Sin actividad = sin mensajes en las últimas {data.inactividad_horas}h (configurable en la pestaña Encuesta)
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4 mb-6">
              {[
                {
                  label: "Conversaciones asignadas", valor: totales.asignadas, tid: "stat-total-asignadas", metric: "asignadas",
                  grad: "from-slate-700 to-slate-900", Icon: MessagesSquare,
                  sub: `${ejecutivos.length} ejecutivo${ejecutivos.length === 1 ? "" : "s"}`
                },
                {
                  label: "Total asignaciones", valor: totales.total_atendidas, tid: "stat-total-atendidas", metric: "total_atendidas",
                  grad: "from-indigo-600 to-violet-700", Icon: History,
                  sub: "histórico (desglose por usuario en la tabla)"
                },
                {
                  label: "Activas", valor: totales.activas, tid: "stat-total-activas", metric: "activas",
                  grad: "from-emerald-500 to-green-600", Icon: Activity,
                  sub: totales.asignadas ? `${Math.round((totales.activas / totales.asignadas) * 100)}% del total` : "—"
                },
                {
                  label: "Sin actividad", valor: totales.sin_actividad, tid: "stat-total-sin-actividad", metric: "sin_actividad",
                  grad: "from-amber-500 to-orange-600", Icon: Clock,
                  sub: totales.asignadas ? `${Math.round((totales.sin_actividad / totales.asignadas) * 100)}% del total` : "—"
                },
                {
                  label: "Sin encuesta", valor: totales.sin_encuesta, tid: "stat-total-sin-encuesta", metric: "sin_encuesta",
                  grad: "from-rose-500 to-red-600", Icon: ClipboardX,
                  sub: totales.asignadas ? `${Math.round((totales.sin_encuesta / totales.asignadas) * 100)}% sin medir` : "—"
                },
                {
                  label: "Reabiertas", valor: totales.reabiertas, tid: "stat-total-reabiertas", metric: "reabiertas",
                  grad: "from-orange-500 to-amber-600", Icon: RotateCcw,
                  sub: totales.asignadas ? `${Math.round((totales.reabiertas / totales.asignadas) * 100)}% del total` : "—"
                },
                {
                  label: "Encuestas enviadas", valor: totales.enviadas, tid: "stat-total-encuestas", metric: "encuestas_enviadas",
                  grad: "from-sky-500 to-blue-600", Icon: ClipboardCheck,
                  sub: totales.enviadas ? `${Math.round((totales.solucionadas / totales.enviadas) * 100)}% solucionadas` : "sin encuestas"
                },
                {
                  label: "Solucionadas", valor: totales.solucionadas, tid: "stat-total-solucionadas", metric: "solucionadas",
                  grad: "from-green-600 to-teal-600", Icon: CheckCircle2,
                  sub: totales.enviadas ? `de ${totales.enviadas} encuestas` : "—"
                }
              ].map(({ label, valor, tid, grad, Icon, sub, metric }) => (
                <div key={tid}
                  onClick={() => toggleDetalle(metric)}
                  role="button"
                  title={detalleMetric === metric ? "Ocultar detalle" : "Ver detalle de las conversaciones"}
                  className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${grad} text-white p-4 shadow-md transition-transform duration-200 hover:scale-[1.03] hover:shadow-lg cursor-pointer ${detalleMetric === metric ? "ring-4 ring-offset-2 ring-slate-400 dark:ring-offset-slate-900" : ""}`}
                  data-testid={`${tid}-card`}>
                  <div className="absolute -right-4 -top-4 opacity-15">
                    <Icon className="w-20 h-20" />
                  </div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold leading-none" data-testid={tid}>{valor}</p>
                    <p className="text-xs font-medium text-white/90 mt-1.5">{label}</p>
                    <p className="text-[11px] text-white/70 mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : (
              <div className="border rounded-lg overflow-auto max-h-[60vh]" data-testid="stats-ejecutivos-table">
                <Table className="border-separate border-spacing-0 [&_td]:border-b [&_td]:border-r [&_td]:border-slate-200 dark:[&_td]:border-slate-700 [&_td:last-child]:border-r-0 [&_td]:text-center">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className={`${thCls} text-left`}>Ejecutivo</TableHead>
                      <TableHead className={thCls}>Asignadas</TableHead>
                      <TableHead className={thCls}>Activas</TableHead>
                      <TableHead className={thCls}>Sin actividad</TableHead>
                      <TableHead className={thCls}>Tiempo promedio de atención</TableHead>
                      <TableHead className={thCls}>🔄 Reabiertas</TableHead>
                      <TableHead className={thCls}>Total atendidas (histórico)</TableHead>
                      <TableHead className={thCls}>🏁 Concluidas (histórico)</TableHead>
                      <TableHead className={thCls}>Sin encuesta</TableHead>
                      <TableHead className={thCls}>Encuestas enviadas</TableHead>
                      <TableHead className={thCls}>✅ Solucionadas</TableHead>
                      <TableHead className={thCls}>🟡 Inconclusas</TableHead>
                      <TableHead className={thCls}>🔴 No solucionadas</TableHead>
                      <TableHead className={thCls}>Sin respuesta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ejecutivos.map((e) => (
                      <Fragment key={e.executive_id}>
                      <TableRow className="odd:bg-white even:bg-slate-50/60 dark:odd:bg-transparent dark:even:bg-slate-800/30" data-testid={`stats-row-${e.executive_id}`}>
                        <TableCell className="font-medium !text-left">
                          <button
                            type="button"
                            onClick={() => setExpanded((p) => ({ ...p, [e.executive_id]: !p[e.executive_id] }))}
                            className="flex items-center gap-1 hover:text-green-700"
                            data-testid={`stats-expand-${e.executive_id}`}
                            title="Ver desglose"
                          >
                            {expanded[e.executive_id] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            {e.executive_name}
                          </button>
                        </TableCell>
                        <TableCell>{e.asignadas}</TableCell>
                        <TableCell className="text-green-600 font-semibold">{e.activas}</TableCell>
                        <TableCell className={e.sin_actividad > 0 ? "text-amber-600 font-semibold" : ""}>{e.sin_actividad}</TableCell>
                        <TableCell>{fmtTiempo(e.tiempo_promedio_min)}</TableCell>
                        <TableCell className={(e.reabiertas || 0) > 0 ? "text-orange-600 font-semibold" : ""}>{e.reabiertas || 0}<Delta actual={e.reabiertas || 0} anterior={e.prev?.reabiertas} malo /></TableCell>
                        <TableCell className="text-indigo-600 font-semibold">{e.total_atendidas || 0}</TableCell>
                        <TableCell className="text-teal-600 font-semibold">{e.concluidas || 0}</TableCell>
                        <TableCell className={(e.sin_encuesta || 0) > 0 ? "text-rose-600 font-semibold" : ""}>{e.sin_encuesta || 0}</TableCell>
                        <TableCell>{e.encuestas_enviadas}<Delta actual={e.encuestas_enviadas} anterior={e.prev?.encuestas_enviadas} /></TableCell>
                        <TableCell className="text-emerald-600 font-semibold">{e.solucionadas}<Delta actual={e.solucionadas} anterior={e.prev?.solucionadas} /></TableCell>
                        <TableCell className={e.inconclusas > 0 ? "text-amber-600" : ""}>{e.inconclusas}<Delta actual={e.inconclusas} anterior={e.prev?.inconclusas} malo /></TableCell>
                        <TableCell className={e.no_solucionadas > 0 ? "text-red-600 font-semibold" : ""}>{e.no_solucionadas}<Delta actual={e.no_solucionadas} anterior={e.prev?.no_solucionadas} malo /></TableCell>
                        <TableCell className="text-slate-400">{e.sin_respuesta}<Delta actual={e.sin_respuesta} anterior={e.prev?.sin_respuesta} malo /></TableCell>
                      </TableRow>
                      {expanded[e.executive_id] && (
                        <TableRow key={`${e.executive_id}-desglose`} className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50" data-testid={`stats-desglose-${e.executive_id}`}>
                          <TableCell colSpan={14} className="!text-left px-6 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                              <div>
                                <p className="font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Conversaciones — {e.executive_name}</p>
                                <ul className="space-y-0.5 text-slate-600 dark:text-slate-400">
                                  <li>• Asignadas actualmente: <b>{e.asignadas}</b> ({e.activas} activas · {e.sin_actividad} sin actividad)</li>
                                  <li>• Tiempo promedio de atención: <b>{fmtTiempo(e.tiempo_promedio_min)}</b></li>
                                  <li>• Reabiertas en el período: <b>{e.reabiertas || 0}</b></li>
                                  <li>• Total atendidas a lo largo del tiempo: <b>{e.total_atendidas || 0}</b></li>
                                  <li>• Concluidas a lo largo del tiempo: <b>{e.concluidas || 0}</b></li>
                                </ul>
                              </div>
                              <div>
                                <p className="font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Encuestas de satisfacción</p>
                                <ul className="space-y-0.5 text-slate-600 dark:text-slate-400">
                                  <li>• Enviadas: <b>{e.encuestas_enviadas}</b> · Sin encuesta: <b>{e.sin_encuesta || 0}</b></li>
                                  <li>• ✅ Solucionadas: <b>{e.solucionadas}</b> · 🟡 Inconclusas: <b>{e.inconclusas}</b></li>
                                  <li>• 🔴 No solucionadas: <b>{e.no_solucionadas}</b> · Sin respuesta: <b>{e.sin_respuesta}</b></li>
                                </ul>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                    ))}
                    {ejecutivos.length === 0 && (
                      <TableRow><TableCell colSpan={14} className="py-8 text-slate-500">Sin datos en este período</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {detalleMetric && (
              <div className="mt-6" data-testid="stats-detalle-seccion">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Detalle: {METRIC_LABELS[detalleMetric]}
                    {detalleData && <span className="text-sm font-normal text-slate-500 ml-2">({detalleData.total} conversaciones)</span>}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => { setDetalleMetric(null); setDetalleData(null); }} data-testid="stats-detalle-cerrar">
                    <X className="w-4 h-4 mr-1" />Cerrar
                  </Button>
                </div>
                {loadingDetalle ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : (
                  <div className="border rounded-lg overflow-auto max-h-[50vh]" data-testid="stats-detalle-table">
                    <Table className="border-separate border-spacing-0 [&_td]:border-b [&_td]:border-r [&_td]:border-slate-200 dark:[&_td]:border-slate-700 [&_td:last-child]:border-r-0">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className={`${thCls} text-left`}>Cliente</TableHead>
                          <TableHead className={`${thCls} text-left`}>Teléfono</TableHead>
                          <TableHead className={`${thCls} text-left`}>Ejecutivo</TableHead>
                          <TableHead className={`${thCls} text-left`}>Fecha</TableHead>
                          {(detalleData?.rows || []).some((r) => r.detalle) && <TableHead className={`${thCls} text-left`}>Resultado</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detalleData?.rows || []).map((r, i) => (
                          <TableRow key={i} className="odd:bg-white even:bg-slate-50/60 dark:odd:bg-transparent dark:even:bg-slate-800/30">
                            <TableCell className="font-medium">{r.cliente || "—"}</TableCell>
                            <TableCell>{r.telefono || "—"}</TableCell>
                            <TableCell>{r.ejecutivo || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap">{r.fecha ? new Date(r.fecha).toLocaleString() : "—"}</TableCell>
                            {(detalleData?.rows || []).some((x) => x.detalle) && <TableCell>{r.detalle || "—"}</TableCell>}
                          </TableRow>
                        ))}
                        {(detalleData?.rows || []).length === 0 && (
                          <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-500">Sin conversaciones para esta métrica</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {isAdmin && (
          <TabsContent value="encuesta">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className={`flex items-center justify-between rounded-lg border p-3 ${encuesta.is_active ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200" : "bg-slate-100 dark:bg-slate-800 border-slate-200"}`}>
                    <div>
                      <Label className="font-semibold">{encuesta.is_active ? "Encuesta encendida ✅" : "Encuesta apagada ⛔"}</Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {encuesta.is_active ? "Los ejecutivos pueden enviarla desde la conversación" : "El botón se oculta en la Bandeja y no se puede enviar"}
                      </p>
                    </div>
                    <Switch checked={!!encuesta.is_active} disabled={!isAdmin}
                      onCheckedChange={(v) => setEncuesta({ ...encuesta, is_active: v })} data-testid="encuesta-activa-switch" />
                  </div>
                  <div>
                    <Label>Pregunta de la encuesta</Label>
                    <Textarea rows={3} value={encuesta.pregunta} disabled={!isAdmin}
                      onChange={(e) => setEncuesta({ ...encuesta, pregunta: e.target.value })} data-testid="encuesta-pregunta" />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0">1️⃣</span>
                      <Input value={encuesta.opcion_solucionada} disabled={!isAdmin}
                        onChange={(e) => setEncuesta({ ...encuesta, opcion_solucionada: e.target.value })} data-testid="encuesta-op1" />
                      <Badge className="bg-emerald-500 shrink-0">Solucionada</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="shrink-0">2️⃣</span>
                      <Input value={encuesta.opcion_inconclusa} disabled={!isAdmin}
                        onChange={(e) => setEncuesta({ ...encuesta, opcion_inconclusa: e.target.value })} data-testid="encuesta-op2" />
                      <Badge className="bg-amber-500 shrink-0">Inconclusa</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="shrink-0">3️⃣</span>
                      <Input value={encuesta.opcion_no_solucionada} disabled={!isAdmin}
                        onChange={(e) => setEncuesta({ ...encuesta, opcion_no_solucionada: e.target.value })} data-testid="encuesta-op3" />
                      <Badge className="bg-red-500 shrink-0">No solucionada</Badge>
                    </div>
                  </div>
                  <div>
                    <Label>Mensaje de agradecimiento</Label>
                    <Input value={encuesta.mensaje_gracias} disabled={!isAdmin}
                      onChange={(e) => setEncuesta({ ...encuesta, mensaje_gracias: e.target.value })} data-testid="encuesta-gracias" />
                  </div>
                  <div>
                    <Label>Umbral "sin actividad" (horas)</Label>
                    <Input type="number" min={1} className="w-32" value={encuesta.inactividad_horas} disabled={!isAdmin}
                      onChange={(e) => setEncuesta({ ...encuesta, inactividad_horas: e.target.value })} data-testid="encuesta-inactividad" />
                    <p className="text-xs text-slate-500 mt-1">Conversaciones asignadas sin mensajes en estas horas cuentan como "sin actividad"</p>
                  </div>
                  <div>
                    <Label>Alerta de reabierta sin atención (minutos)</Label>
                    <Input type="number" min={0} className="w-32" value={encuesta.reapertura_alerta_minutos} disabled={!isAdmin}
                      onChange={(e) => setEncuesta({ ...encuesta, reapertura_alerta_minutos: e.target.value })} data-testid="encuesta-reapertura-alerta" />
                    <p className="text-xs text-slate-500 mt-1">Si una conversación reabierta pasa estos minutos sin respuesta del ejecutivo, se avisa a su supervisor (0 = desactivada)</p>
                  </div>
                  {isAdmin ? (
                    <Button onClick={guardarEncuesta} disabled={savingEncuesta} className="bg-green-600 hover:bg-green-700" data-testid="encuesta-guardar-btn">
                      {savingEncuesta ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Guardar Configuración
                    </Button>
                  ) : (
                    <p className="text-xs text-slate-500">Solo los administradores pueden modificar esta configuración.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-slate-50 dark:bg-slate-800/50">
                <CardContent className="pt-6">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-green-600" />Así la verá el cliente:</p>
                  <div className="bg-white dark:bg-slate-900 border rounded-lg p-4 text-sm whitespace-pre-line" data-testid="encuesta-preview">
                    {encuesta.pregunta}
                    {"\n\n"}1️⃣ {encuesta.opcion_solucionada}
                    {"\n"}2️⃣ {encuesta.opcion_inconclusa}
                    {"\n"}3️⃣ {encuesta.opcion_no_solucionada}
                    {"\n\n"}Responda con el *número* de su elección.
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    El ejecutivo la envía con el botón "Encuesta" en la conversación. Según la respuesta, la conversación
                    se marca como solucionada, inconclusa o no solucionada. Si el cliente no responde, queda como
                    "enviada sin respuesta" y el estado no cambia.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
